import { useMemo, useState } from "react";
import {
  SimpleTable,
  Badge,
  Button,
  Input,
  InvoiceDropdown,
  ChannelDropdown,
  PaymentProfilesDropdown,
} from "@components";
import { usePaymentProfilesContext } from "@context/PaymentProfilesContext";
import Progress from "./Progress";
import { cn, formatAmount } from "@utils";
import { statusLabel } from "../../PaymentRequestDrawers/utils";
import { ChevronUpIcon } from "@heroicons/react/24/solid";
import { EnvelopeIcon, DevicePhoneMobileIcon } from "@heroicons/react/24/solid";
import { Search, Download } from "react-bootstrap-icons";
import useConfirmSendData from "../hooks/useConfirmSendData";
import downloadCsv from "./downloadCsv";
import { PdfDropdown } from "@components/paymentTemplates";
import { useAllUsers } from "@api/usersController";
import RecipientListRow from "../../../../../portal-template/components/SmartNoteModal/RecipientListRow";
import { MAX_EMAIL_COPIES } from "../../../../../portal-template/components/SmartNoteModal/actionAmendments";

const statusColors = {
  ready: "blue",
  done: "green",
  sending: "blue",
  sendingFailed: "red",
  notSent: "zinc",
};

export default function ConfirmSendStep({
  rows,
  phase,
  invoicesRequested,
  isCustom,
  customAmount,
  control,
  watch,
  smsSelected,
  emailSelected,
  onSend,
  onBack,
  onDone,
  isSingleCustomer,
  emailEnabled,
  selection,
  initializedRef,
  // The send's current recipients (e.g. an edited action step's payload) — they seed
  // the single-customer contact fields in place of the account row's record details,
  // and its Cc/Bcc lists seed the copy rows.
  initialContacts = null,
  // The customer the send is for, offered first in the copy rows' dropdown.
  customer = null,
  // Accept-an-action mode: the CTA accepts the suggestion with the edits applied
  // rather than sending a batch; the caller owns the in-flight state.
  acceptMode = false,
  acceptPending = false,
}) {
  const [showAccounts, setShowAccounts] = useState(false);
  const { profiles: paymentProfiles } = usePaymentProfilesContext();
  // Copies exist only on an AI-suggested request (the step contract carries them); the batch send has none.
  const [cc, setCc] = useState(() => initialContacts?.cc ?? []);
  const [bcc, setBcc] = useState(() => initialContacts?.bcc ?? []);
  // The copy rows are opt-in: shown when the suggestion already carries copies, or once the user asks.
  const [copiesOpen, setCopiesOpen] = useState(
    () => (initialContacts?.cc?.length ?? 0) + (initialContacts?.bcc?.length ?? 0) > 0,
  );
  const [editingCopies, setEditingCopies] = useState({ cc: false, bcc: false });
  const { data: users = [] } = useAllUsers();

  const {
    eligibleRows,
    filteredRows,
    effectiveAccounts,
    selectedCount,
    isRowSelected,
    toggleRowSelection,
    selectAccounts,
    deselectAccounts,
    deselectAll,
    initializeSelection,
    searchTerm,
    setSearchTerm,
    contactEmail,
    setContactEmail,
    contactPhone,
    setContactPhone,
    emailInputRef,
    phoneInputRef,
    emailValid,
    phoneValid,
    contactsValid,
    selectedAmount,
    sentCount,
    emailCount,
    smsCount,
    emailsSent,
    smsSent,
  } = useConfirmSendData({
    rows,
    isCustom,
    customAmount,
    smsSelected,
    emailSelected,
    isSingleCustomer,
    selection,
    initializedRef,
    initialContacts,
  });

  /* ---- table config ---- */
  const tableConfig = useMemo(
    () => ({
      columns: [
        {
          title: "Customer",
          width: "200px",
          render: (r) => <span title={r.customerName}>{r.customerName}</span>,
        },
        {
          title: "Account Code",
          width: "130px",
          render: (r) => <span title={r.reference}>{r.reference}</span>,
        },
        {
          title: "Amount",
          width: "120px",
          alignment: "right",
          render: (r) => (
            <span title={formatAmount(r.amount || r.minimumAmount)}>
              {formatAmount(r.amount || r.minimumAmount)}
            </span>
          ),
          shouldShow: () => invoicesRequested !== "None",
        },
        {
          title: "Phone",
          width: "150px",
          render: (r) => <span title={r.phone}>{r.phone}</span>,
          shouldShow: () => smsSelected,
        },
        {
          title: "Email",
          render: (r) => <span title={r.email}>{r.email}</span>,
          shouldShow: () => emailSelected,
        },
        {
          title: "Status",
          width: "140px",
          sticky: true,
          render: (r) => (
            <Badge color={statusColors[r.status]}>
              {statusLabel(r.status)}
            </Badge>
          ),
          shouldShow: () => phase !== "review",
        },
      ],
    }),
    [smsSelected, emailSelected, invoicesRequested, phase],
  );

  /* ---- send handler ---- */
  const handleSend = () => {
    onSend(effectiveAccounts, acceptMode ? { cc, bcc } : null);
  };

  const showCopies = acceptMode && isSingleCustomer && emailSelected;
  const copyEditing = copiesOpen && (editingCopies.cc || editingCopies.bcc);
  const openCopies = () => setCopiesOpen(true);
  // Closing the rows also clears them: the request goes out with no copies.
  const removeCopies = () => {
    setCc([]);
    setBcc([]);
    setEditingCopies({ cc: false, bcc: false });
    setCopiesOpen(false);
  };
  const copyRowEditing = (row) => (editing) =>
    setEditingCopies((prev) => (prev[row] === editing ? prev : { ...prev, [row]: editing }));
  // Every address already on the request, so a copy row never offers or accepts a repeat.
  const onRequest = useMemo(
    () => new Set([contactEmail, ...cc, ...bcc].filter(Boolean).map((a) => a.toLowerCase())),
    [contactEmail, cc, bcc],
  );
  // The dropdown: the account's addresses first, then the merchant's staff by name.
  const copySuggestions = useMemo(() => {
    const account = [customer?.email, eligibleRows[0]?.email]
      .filter(Boolean)
      .filter((email, i, all) => all.findIndex((e) => e.toLowerCase() === email.toLowerCase()) === i)
      .map((email) => ({ email, name: customer?.name ?? eligibleRows[0]?.customerName }));
    const staff = users
      .filter((user) => user.email)
      .map((user) => ({ email: user.email, name: user.name }))
      .sort((a, b) => (a.name ?? a.email).localeCompare(b.name ?? b.email));
    return [
      { label: "Account", addresses: account },
      { label: "Staff", addresses: staff },
    ];
  }, [customer?.email, customer?.name, eligibleRows, users]);

  const handleExport = () => {
    const columns = [
      { label: "Customer", value: (r) => r.customerName },
      { label: "Account Code", value: (r) => r.reference },
      ...(emailSelected
        ? [{ label: "Email", value: (r) => r.email || "" }]
        : []),
      ...(smsSelected ? [{ label: "Phone", value: (r) => r.phone || "" }] : []),
      ...(invoicesRequested !== "None"
        ? [{ label: "Amount", value: (r) => r.amount || r.minimumAmount }]
        : []),
      ...(phase !== "review"
        ? [{ label: "Status", value: (r) => statusLabel(r.status) }]
        : []),
    ];
    downloadCsv("batch_send", columns, filteredRows);
  };

  const getModalMainText = () => {
    const numberOfAccountsOrCustomerText = isSingleCustomer
      ? eligibleRows[0]?.customerName
      : `${selectedCount} ${selectedCount === 1 ? "account" : "accounts"}`;
    const invoicesRequestedIsNone = invoicesRequested === "None";
    return (
      <p className="text-lg text-zinc-900">
        {invoicesRequestedIsNone ? (
          <>
            Sending to <strong>{numberOfAccountsOrCustomerText}</strong>
          </>
        ) : (
          <>
            Requesting <strong>{formatAmount(selectedAmount)}</strong> from{" "}
            <strong>{numberOfAccountsOrCustomerText}</strong>
          </>
        )}
      </p>
    );
  };

  const isSending = phase === "processing";
  const isComplete = phase === "complete";
  const showProgress = isSending || isComplete;

  return (
    <>
      {/* Pills */}
      <div className="shrink-0 flex flex-wrap gap-2 px-4 py-3 border-b border-zinc-200">
        <InvoiceDropdown
          control={control}
          watch={watch}
          variant="pill"
          disabled
        />
        {emailEnabled && ( 
          <PdfDropdown
            control={control}
            watch={watch}
            variant="pill"
            disabled
          />
        )}
        <ChannelDropdown
          emailEnabled={emailSelected}
          smsEnabled={smsSelected}
          variant="pill"
          disabled
        />
        <PaymentProfilesDropdown
          control={control}
          watch={watch}
          paymentProfiles={paymentProfiles}
          variant="pill"
          disabled
        />
      </div>

      {/* Summary */}
      <div className="shrink-0 flex flex-col items-center gap-5 px-5 pb-6">
        <div className="w-full pt-6 px-5 pb-2 flex items-center justify-center">
          {showProgress ? (
            <Progress
              current={sentCount}
              end={selectedCount}
              status={isComplete ? "Complete" : "Processing"}
            />
          ) : (
            getModalMainText()
          )}
        </div>
        {isSingleCustomer && !showProgress ? (
          <>
            <div className="flex gap-4 justify-center">
            {emailSelected && (
              <div className="w-56 flex flex-col gap-1">
                <label className="flex items-center justify-center gap-1.5 text-xs text-zinc-500">
                  <EnvelopeIcon className="size-4" />1 email will be sent to
                </label>
                <Input
                  type="email"
                  innerRef={emailInputRef}
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="Email address"
                  className={cn(
                    "text-sm text-center",
                    contactEmail &&
                      !emailValid &&
                      "border-red-300 focus:ring-red-500",
                  )}
                />
                {contactEmail && !emailValid && (
                  <span className="text-xs text-red-500 text-center">
                    Invalid email address
                  </span>
                )}
                {showCopies && !copiesOpen && (
                  <button
                    type="button"
                    onClick={openCopies}
                    disabled={acceptPending}
                    className="self-center text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-50"
                  >
                    Add copies (Cc / Bcc)
                  </button>
                )}
              </div>
            )}
            {smsSelected && (
              <div className="w-56 flex flex-col gap-1">
                <label className="flex items-center justify-center gap-1.5 text-xs text-zinc-500">
                  <DevicePhoneMobileIcon className="size-4" />1 SMS will be sent
                  to
                </label>
                <Input
                  type="tel"
                  innerRef={phoneInputRef}
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="Phone number"
                  className={cn(
                    "text-sm text-center",
                    contactPhone &&
                      !phoneValid &&
                      "border-red-300 focus:ring-red-500",
                  )}
                />
                {contactPhone && !phoneValid && (
                  <span className="text-xs text-red-500 text-center">
                    Invalid phone number
                  </span>
                )}
              </div>
            )}
            </div>
            {/* Copies get the full width: pills need room for several addresses per line. */}
            {showCopies && copiesOpen && (
              <div className="w-full max-w-2xl rounded-lg border border-zinc-200 px-3 py-2 text-xs">
                <div className="flex items-center justify-between px-2 pb-1">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-zinc-400">
                    Copies on the email
                  </span>
                  <button
                    type="button"
                    onClick={removeCopies}
                    disabled={acceptPending}
                    className="text-[11px] font-medium text-zinc-500 hover:text-red-600 disabled:opacity-50"
                  >
                    Remove copies
                  </button>
                </div>
                <RecipientListRow
                  label="Cc:"
                  addresses={cc}
                  onChange={setCc}
                  max={MAX_EMAIL_COPIES}
                  emptyText="Add an address or a colleague"
                  disabled={acceptPending}
                  suggestions={copySuggestions}
                  excluded={onRequest}
                  onEditingChange={copyRowEditing("cc")}
                />
                <RecipientListRow
                  label="Bcc:"
                  addresses={bcc}
                  onChange={setBcc}
                  max={MAX_EMAIL_COPIES}
                  emptyText="Add an address or a colleague"
                  disabled={acceptPending}
                  suggestions={copySuggestions}
                  excluded={onRequest}
                  onEditingChange={copyRowEditing("bcc")}
                />
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center gap-8 text-xs text-zinc-500">
            {emailSelected && (!showProgress || emailCount > 0) && (
              <span className="inline-flex items-center gap-1.5">
                <EnvelopeIcon className="size-4" />
                {showProgress
                  ? `${emailsSent}/${emailCount} ${emailCount === 1 ? "email" : "emails"} sent`
                  : `${emailCount} ${emailCount === 1 ? "email" : "emails"} will be sent`}
              </span>
            )}
            {smsSelected && (!showProgress || smsCount > 0) && (
              <span className="inline-flex items-center gap-1.5">
                <DevicePhoneMobileIcon className="size-4" />
                {showProgress
                  ? `${smsSent}/${smsCount} SMS sent`
                  : `${smsCount} SMS will be sent`}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Show Accounts toggle + table */}
      {!isSingleCustomer && (
        <div className="flex-1 min-h-0 flex flex-col">
          <button
            type="button"
            onClick={() => setShowAccounts((prev) => !prev)}
            className="shrink-0 flex items-center justify-center gap-1 px-5 pb-6 text-xs font-medium text-primary hover:text-primary/80"
          >
            {showAccounts ? "Hide" : "Show"} Accounts
            <ChevronUpIcon
              className={cn(
                "size-4 transition-transform",
                !showAccounts && "rotate-180",
              )}
            />
          </button>

          {showAccounts && (
            <div className="flex-1 min-h-0 overflow-auto border-t border-zinc-200">
              <SimpleTable
                data={filteredRows}
                config={tableConfig}
                noFooter
                rowHeight="sm"
                compact
                embedded
                lazyLoad
                isRowSelected={isRowSelected}
                onToggleRow={toggleRowSelection}
                totalRows={eligibleRows.length}
                totalSelectedRows={selectedCount}
                displayedRows={
                  searchTerm.trim() ? filteredRows.length : undefined
                }
                searchTerm={searchTerm.trim() || undefined}
                onSelectAll={() =>
                  searchTerm.trim()
                    ? selectAccounts(filteredRows)
                    : initializeSelection(eligibleRows)
                }
                onDeselectAll={() =>
                  searchTerm.trim()
                    ? deselectAccounts(filteredRows)
                    : deselectAll()
                }
                selectionDisabled={isSending || isComplete}
                headerExtra={
                  <div className="flex items-center gap-2">
                    <div className="relative w-56">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 size-4 z-10" />
                      <Input
                        type="text"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 py-1"
                      />
                    </div>
                  </div>
                }
              />
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="shrink-0 p-4 flex items-center justify-between border-t border-zinc-200">
        {!isComplete ? (
          <>
            <Button
              onClick={onBack}
              color="secondary"
              disabled={isSending || acceptPending}
            >
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Button onClick={handleExport} color="secondary">
                <Download className="mr-1.5" />
                Export
              </Button>
              <Button
                color="primary"
                onClick={handleSend}
                disabled={
                  isSending || acceptPending || selectedCount === 0 || !contactsValid || copyEditing
                }
              >
                {acceptMode
                  ? acceptPending
                    ? "Accepting..."
                    : "Accept"
                  : isSending
                    ? "Sending..."
                    : `Send to ${selectedCount} ${selectedCount === 1 ? "account" : "accounts"}`}
              </Button>
            </div>
          </>
        ) : (
          <>
            <Button
              onClick={handleExport}
              color="secondary"
              className="ml-auto mr-2"
            >
              <Download className="mr-1.5" />
              Export
            </Button>
            <Button color="primary" onClick={onDone}>
              Done
            </Button>
          </>
        )}
      </div>
    </>
  );
}
